/**
 * FormsListPage — admin list of every active workflow_form.
 *
 * Route: /setup/forms
 *
 * Click a row → edit. Top-right "+ New Form" → /setup/forms/new.
 * Forms here aren't yet attached to workflows on this page; that
 * happens in the Create-Workflow wizard (A4) which lets admin pick
 * an existing form OR create one inline.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../api';

const COLORS = {
  brand: '#3F66A0',
  border: '#e2e8f0', ink: '#0f172a', inkSoft: '#475569', inkMuted: '#94a3b8',
};

export default function FormsListPage() {
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.listForms()
      .then((r) => setForms(r.forms || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="page" style={{ padding: '28px 32px 48px', maxWidth: 1080, margin: '0 auto' }}>
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 18,
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: COLORS.ink, letterSpacing: '-0.02em' }}>Forms</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: COLORS.inkMuted }}>
            Reusable form schemas. Attach one to a workflow to expose it as a "+ Submit" button on a module's landing page.
          </p>
        </div>
        <button type="button" onClick={() => navigate('/setup/forms/new')}
          style={{ padding: '10px 22px', borderRadius: 10, border: 'none',
            background: COLORS.brand, color: '#fff', fontSize: 14, fontWeight: 700,
            cursor: 'pointer', boxShadow: '0 2px 6px rgba(63,102,160,0.25)' }}>
          + New Form
        </button>
      </header>

      {error && (
        <div style={{ padding: '12px 16px', borderRadius: 10, marginBottom: 14,
          background: '#fef3c7', border: '1px solid #fcd34d', color: '#78350f',
          fontSize: 13, fontWeight: 600 }}>{error}</div>
      )}

      {loading ? (
        <div style={{ color: COLORS.inkMuted }}>Loading forms…</div>
      ) : forms.length === 0 ? (
        <div style={{
          padding: 40, borderRadius: 14, border: `1px dashed ${COLORS.border}`,
          textAlign: 'center', color: COLORS.inkMuted, fontSize: 14,
        }}>
          No forms yet. Click <strong style={{ color: COLORS.ink }}>+ New Form</strong> to create your first one.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {forms.map((f) => (
            <Link key={f.id} to={`/setup/forms/${f.id}`} style={{ textDecoration: 'none' }}>
              <div style={{
                padding: '14px 18px', borderRadius: 12, border: `1px solid ${COLORS.border}`,
                background: '#fff', cursor: 'pointer', transition: 'border-color 0.15s, box-shadow 0.15s',
                display: 'flex', alignItems: 'center', gap: 16,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = COLORS.brand; e.currentTarget.style.boxShadow = '0 2px 6px rgba(63,102,160,0.10)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.ink }}>{f.name}</div>
                  {f.description && (
                    <div style={{ fontSize: 13, color: COLORS.inkSoft, marginTop: 2 }}>{f.description}</div>
                  )}
                  <div style={{ fontSize: 11, color: COLORS.inkMuted, marginTop: 4, fontFamily: 'monospace' }}>
                    v{f.version} · created {f.created_at?.slice(0, 10)}
                  </div>
                </div>
                <span style={{ fontSize: 18, color: COLORS.inkMuted }}>→</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
